import { Icon, Icons } from '@/components/icon';
import { cn } from '@/lib/utils';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { Header } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import * as React from 'react';
import { P2 } from '../ui/text';
import type { PinningShadowState } from './types';
import { type PinningShadowDirection, getCommonPinningStyles } from './utils';

/**
 * Renders a gradient-based shadow for pinned columns.
 * Uses a pseudo-element approach to avoid vertical bleeding that box-shadow causes.
 */
function PinningShadow({ direction }: { direction: PinningShadowDirection }) {
  if (!direction) {
    return null;
  }

  return (
    <div
      className="pointer-events-none absolute top-0 h-full w-3"
      style={{
        ...(direction === 'left'
          ? { right: 0, transform: 'translateX(100%)' }
          : { left: 0, transform: 'translateX(-100%)' }),
        background:
          direction === 'left'
            ? 'linear-gradient(to right, rgba(0, 44, 42, 0.06) 0%, rgba(0, 44, 42, 0.02) 50%, transparent 100%)'
            : 'linear-gradient(to left, rgba(0, 44, 42, 0.06) 0%, rgba(0, 44, 42, 0.02) 50%, transparent 100%)',
      }}
    />
  );
}

interface TableHeaderCellProps<TData> {
  header: Header<TData, unknown>;
  enableColumnResizing?: boolean;
  enableColumnReorder?: boolean;
  pinningShadows: PinningShadowState;
}

export function TableHeaderCell<TData>({
  header,
  enableColumnResizing,
  enableColumnReorder,
  pinningShadows,
}: TableHeaderCellProps<TData>) {
  const isPinned = header.column.getIsPinned();
  const isDraggable = enableColumnReorder && !isPinned;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: header.column.id,
    disabled: !isDraggable,
  });

  const canResize = enableColumnResizing && header.column.getCanResize();
  const sortDirection = header.column.getIsSorted();
  const [isHoveringHandle, setIsHoveringHandle] = React.useState(false);

  const isResizing = header.column.getIsResizing();
  const shouldElevate = isResizing || isHoveringHandle || isDragging;

  // Get pinning styles and shadow direction
  const { styles: pinningStyles, shadowDirection } = getCommonPinningStyles(
    header.column,
    pinningShadows,
  );

  const dragStyle: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  React.useEffect(() => {
    document.body.style.cursor = isResizing ? 'col-resize' : 'default';

    return () => {
      document.body.style.cursor = 'default';
    };
  }, [isResizing]);

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'group/header relative z-raised flex flex-none items-center border-primary-5 border-b bg-white px-3 py-3.5 font-medium text-primary-100',
        'border-r',
        shadowDirection && 'border-r-transparent',
        isDraggable && 'cursor-grab active:cursor-grabbing',
      )}
      style={{
        width: `calc(var(--header-${header?.id}-size) * 1px)`,
        left: `calc(var(--header-${header?.id}-start) * 1px)`,
        ...pinningStyles,
        ...(shouldElevate ? { zIndex: 40 } : undefined),
        ...dragStyle,
      }}
      aria-sort={sortDirection ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}
      {...(isDraggable ? attributes : {})}
      {...(isDraggable ? listeners : {})}
    >
      <P2 className="flex flex-1 items-center gap-1 overflow-hidden truncate">
        {header.column.columnDef.meta?.icon && (
          <Icon
            name={Icons[header.column.columnDef.meta.icon]}
            className="shrink-0 text-m text-primary-100"
          />
        )}

        {header.isPlaceholder
          ? null
          : flexRender(header.column.columnDef.header, header.getContext())}
      </P2>
      {/* Resize handle */}
      {canResize && (
        <div
          className="group absolute top-0 right-0 bottom-0 z-dropdown my-auto h-[90%] min-w-1 translate-x-1/2 cursor-col-resize px-1"
          // dnd-kit's PointerSensor activates on pointerdown, which mousedown's
          // stopPropagation doesn't block - without this, resizing a reorderable
          // column also starts a column drag
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => {
            e.stopPropagation();
            header.getResizeHandler()(e);
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
            header.getResizeHandler()(e);
          }}
          onDoubleClick={() => header.column.resetSize()}
          onMouseEnter={() => setIsHoveringHandle(true)}
          onMouseLeave={() => setIsHoveringHandle(false)}
        >
          <div
            data-resize-handle
            className={cn(
              'h-full w-[5px] touch-none select-none rounded-2xl opacity-0 transition-opacity group-hover:opacity-100',
              isResizing && 'bg-highlight-default opacity-100',
              !isResizing && 'group-hover:bg-highlight-default group-hover:opacity-100',
            )}
          />
        </div>
      )}
      <PinningShadow direction={shadowDirection} />
    </div>
  );
}
